import { zodResolver } from '@hookform/resolvers/zod'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AlertDialog } from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Textarea } from '@/components/ui/text-area'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useUserContactMutation } from '@/store/api/Users/Users.query'
import { BackgroundCheckContactUsSchema } from '@/utils/schemas'
import type { BackgroundCheckContactUsType } from '@/utils/schemas'

import { ContactUsAlertDialog } from './components/ContactUsAlertDialog'

export const ContactUs = () => {
  const { t } = useTranslation()
  const [submitContact, { isLoading }] = useUserContactMutation()
  const [isOpen, setIsOpen] = useState(false)
  const form = useForm<BackgroundCheckContactUsType>({
    resolver: zodResolver(BackgroundCheckContactUsSchema),
    defaultValues: {
      comment: '',
    },
  })

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form

  const onSubmit = async (data: any) => {
    try {
      await submitContact({
        message: data.comment,
      })
      setIsOpen(true)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('feedback.toast.formError'))
    }
  }

  return (
    <WeScreen useHeader={false} customViewStyles="max-sm:p-0">
      <div className="flex flex-col gap-4 rounded-lg justify-center h-full items-center">
        <div className="flex flex-col bg-baseColorHighlight gap-4 p-4 rounded-2xl w-2/3 max-md:w-full">
          <p className="text-neutralColor text-2xl max-md:text-xl font-medium">
            {t('dashboard.contactUs.title')}
          </p>
          <p className="text-neutralColor80 text-lg max-md:text-base ">
            {t('dashboard.contactUs.subtitle')}
          </p>
        </div>
        <div className="flex flex-col bg-baseColorHighlight p-4 rounded-2xl w-2/3 max-md:w-full">
          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mb-4"
            >
              <FormField
                control={control}
                name="comment"
                render={({ field }) => (
                  <FormItem className="w-full ">
                    <FormControl className="flex w-full focus:outline-none">
                      <Textarea
                        className="bg-transparent min-h-[250px] rounded-2xl border border-neutralColor30"
                        {...field}
                        placeholder={t('dashboard.contactUs.placeholder')}
                      />
                    </FormControl>
                    <FormMessage data-name="comment" />
                  </FormItem>
                )}
              />

              <Button
                className="text-base font-light tracking-widest flex justify-end w-min"
                isLoading={isLoading}
                disabled={!isDirty}
                type="submit"
                variant={'default'}
              >
                {t('dashboard.contactUs.submit')}
              </Button>
            </form>
          </Form>
        </div>
      </div>
      <AlertDialog open={isOpen}>
        <ContactUsAlertDialog />
      </AlertDialog>
    </WeScreen>
  )
}
